import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/faq-section";
import { PAGE_FAQS } from "@/data/faqs";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openBookingModal } from "@/components/booking-modal";

export default function DenRozhdeniya() {
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "День рождения на крыше в Петербурге — Паруса на крыше",
    description: "Отметьте день рождения на крыше Петербурга. Скидка 20% за 7 дней до и после дня рождения. Панорамный вид, торт в подарок, живая музыка. Ул. Марата, 5.",
    keywords: "день рождения на крыше Петербург, отметить день рождения ресторан СПб, ресторан день рождения скидка, день рождения с видом на город, банкет день рождения Петербург, торт день рождения ресторан СПб",
    image: "/photos/view_sunset.jpg",
    canonical: "/den-rozhdeniya",
  });
  useBreadcrumbs([{ name: "День рождения", path: "/den-rozhdeniya" }]);

  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <div className="w-full bg-background">
      {/* Hero */}
      <section className="relative h-[80vh] w-full flex items-center justify-center overflow-hidden border-b border-primary/20">
        <div className="absolute inset-0 z-0">
          <img src="/photos/view_sunset.webp" alt="День рождения на крыше Петербурга" className="w-full h-full object-cover" style={{ filter: "brightness(0.25) saturate(0.6)", transform: "scale(1.05)" }} loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(5,20,10,0.95) 0%, rgba(10,30,18,0.88) 100%)" }} />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(30px)", transition: "all 1s ease 0.3s" }}>
          <div className="inline-block px-6 py-2 border border-primary/30 mb-8">
            <span className="text-[9px] tracking-[0.3em] uppercase text-accent">Особый повод</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-serif font-light text-foreground mb-6 leading-[1.1]">
            День рождения<br /><span className="text-accent italic">на крыше</span>
          </h1>
          <p className="text-white/60 text-lg font-light mb-10 max-w-xl mx-auto leading-relaxed">
            Панорама Петербурга, живая музыка и скидка 20% — в ваш особенный день
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openBookingModal} className="bg-primary/20 border border-primary text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary hover:text-background transition-colors">
              Забронировать стол
            </button>
            <button onClick={() => document.getElementById("gifts")?.scrollIntoView({ behavior: "smooth" })} className="border border-white/20 text-white/60 px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:border-primary/50 hover:text-accent transition-colors">
              Наши подарки
            </button>
          </div>
        </div>
      </section>

      {/* Discount badge */}
      <section className="py-16 bg-card border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
            <div className="flex items-center gap-6 shrink-0">
              <div className="font-['Cormorant_SC'] text-7xl md:text-9xl font-bold text-primary/20 leading-none select-none">20%</div>
              <div>
                <div className="text-accent text-lg font-medium mb-1">скидка на весь счёт</div>
                <div className="text-muted-foreground text-sm font-light">за 7 дней до и 7 дней после дня рождения</div>
                <div className="text-muted-foreground/50 text-xs mt-2">при оплате наличными, независимо от суммы</div>
              </div>
            </div>
            <div className="w-[1px] h-20 bg-primary/15 hidden md:block" />
            <p className="text-muted-foreground font-light leading-relaxed">
              Просто скажите нам о дне рождения при бронировании — мы сделаем всё остальное. Персональное поздравление, торт от шеф-кондитера и незабываемый вечер с видом на крыши Петербурга.
            </p>
          </div>
        </div>
      </section>

      {/* Gifts */}
      <section id="gifts" className="py-32 bg-background border-b border-primary/10">
        <div className="container mx-auto px-6 md:px-14 max-w-6xl">
          <div className="mb-16">
            <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-4">Что мы дарим</div>
            <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground">Четыре подарка имениннику</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Скидка 20%", desc: "На весь счёт именинника в течение 7 дней до и после дня рождения" },
              { num: "02", title: "Бонусная карта", desc: "Персональная карта гостя ресторана с накопительными бонусами" },
              { num: "03", title: "Торт в подарок", desc: "Авторский десерт от шеф-кондитера с персональной надписью" },
              { num: "04", title: "Поздравление", desc: "Персональное поздравление от всей команды Парусов на крыше" },
            ].map((g) => (
              <div key={g.num} className="border border-primary/20 p-8 bg-card group hover:border-primary/50 transition-colors">
                <div className="font-serif text-4xl font-light text-primary/15 mb-5 group-hover:text-primary/35 transition-colors">{g.num}</div>
                <h3 className="font-['Cormorant_SC'] text-sm tracking-[0.2em] text-accent mb-3">{g.title}</h3>
                <p className="text-xs text-muted-foreground font-light leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why here */}
      <section className="py-32 bg-card border-b border-primary/10 overflow-hidden relative">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 font-serif text-[400px] font-light text-primary/[0.03] select-none pointer-events-none leading-none">ДР</div>
        <div className="container mx-auto px-6 md:px-14 max-w-6xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-[9px] tracking-[0.4em] text-primary/50 uppercase mb-5">Почему здесь</div>
              <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-8 leading-tight">
                «Паруса на крыше» —<br /><span className="text-accent italic">это:</span>
              </h2>
              <ul className="space-y-5">
                {[
                  "Легендарные каораоке-вечеринки и живая музыка",
                  "Панорамный вид на 360° — весь Петербург как на ладони",
                  "Лучший ресторан по версии Time Out",
                  "6-й этаж Olympic Plaza — самый атмосферный адрес города",
                  "Организация праздника под ключ — от декора до артистов",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="text-primary/50 mt-1 shrink-0">✦</span>
                    <span className="text-muted-foreground font-light text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="/photos/view_summer.webp" alt="Вид с крыши Петербурга" className="w-full aspect-[4/3] object-cover border border-primary/15" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <FaqSection items={PAGE_FAQS["/den-rozhdeniya"]} title="Вопросы о дне рождения" />

      {/* CTA */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 text-center max-w-2xl">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-6">Готовы отметить?</h2>
          <p className="text-muted-foreground font-light mb-10 leading-relaxed">
            Забронируйте стол прямо сейчас или оставьте заявку на организацию банкета — мы свяжемся с вами и подберём идеальный вариант.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={openBookingModal} className="bg-primary border-primary border text-background px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:bg-primary/80 transition-colors">
              Забронировать стол
            </button>
            <a href="/banket" className="border border-primary/40 text-accent px-12 py-4 text-[10px] tracking-[0.3em] uppercase font-medium hover:border-primary transition-colors">
              Банкет под ключ
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
